import React from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { useUIStore } from '../../store';

interface ValidationError {
  type: string;
  step_id?: string;
  message: string;
}

interface ValidationWarning {
  type: string;
  step_id?: string;
  message: string;
}

interface ValidationResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  is_valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export const ValidationResultsModal: React.FC<ValidationResultsModalProps> = ({
  isOpen,
  onClose,
  is_valid,
  errors,
  warnings,
}) => {
  const theme = useUIStore((state) => state.theme);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className={`${
          theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        } rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2 className="text-xl font-semibold">Validation Results</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            }`}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Summary */}
          <div
            className={`mb-4 p-4 rounded-lg ${
              is_valid
                ? theme === 'dark'
                  ? 'bg-green-900/20 border border-green-800'
                  : 'bg-green-50 border border-green-200'
                : theme === 'dark'
                ? 'bg-red-900/20 border border-red-800'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p
              className={`font-medium ${
                is_valid
                  ? theme === 'dark'
                    ? 'text-green-400'
                    : 'text-green-700'
                  : theme === 'dark'
                  ? 'text-red-400'
                  : 'text-red-700'
              }`}
            >
              {is_valid
                ? '✓ Pipeline is valid!'
                : '✗ Pipeline has validation errors'}
            </p>
            <p
              className={`text-sm mt-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              {errors.length > 0 && `${errors.length} error(s)`}
              {errors.length > 0 && warnings.length > 0 && ', '}
              {warnings.length > 0 && `${warnings.length} warning(s)`}
            </p>
          </div>

          {/* Info message about saving with errors */}
          {!is_valid && (
            <div
              className={`mb-4 p-3 rounded-lg border ${
                theme === 'dark'
                  ? 'bg-blue-900/20 border-blue-800 text-blue-400'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
            >
              <p className="text-sm">
                <strong>Note:</strong> You can still save pipelines with errors.
                The status will be set to ERROR.
              </p>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="mb-6">
              <h3
                className={`text-lg font-semibold mb-3 flex items-center ${
                  theme === 'dark' ? 'text-red-400' : 'text-red-700'
                }`}
              >
                <AlertCircle className="w-5 h-5 mr-2" />
                Errors
              </h3>
              <div className="space-y-2">
                {errors.map((error, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-red-900/10 border-red-800'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        {error.step_id && (
                          <p
                            className={`text-sm font-medium mb-1 ${
                              theme === 'dark' ? 'text-red-400' : 'text-red-700'
                            }`}
                          >
                            Step: {error.step_id}
                          </p>
                        )}
                        <p
                          className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {error.message}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                          }`}
                        >
                          Type: {error.type}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div>
              <h3
                className={`text-lg font-semibold mb-3 flex items-center ${
                  theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'
                }`}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Warnings
              </h3>
              <div className="space-y-2">
                {warnings.map((warning, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-yellow-900/10 border-yellow-800'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        {warning.step_id && (
                          <p
                            className={`text-sm font-medium mb-1 ${
                              theme === 'dark'
                                ? 'text-yellow-400'
                                : 'text-yellow-700'
                            }`}
                          >
                            Step: {warning.step_id}
                          </p>
                        )}
                        <p
                          className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {warning.message}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                          }`}
                        >
                          Type: {warning.type}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No issues */}
          {errors.length === 0 && warnings.length === 0 && (
            <p
              className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              No validation issues found. Your pipeline looks good!
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex justify-end px-6 py-4 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

